/* eslint-disable max-len */
import { lazy } from 'react';

import { retry } from 'utils';
import { COUNTRIES } from 'constants/country-data';
import { isSllrUser } from 'utils/helpers';
import { userHasChatsAccess } from 'utils/chats';
import {
  getOrdersAndPickupsAccess,
  getPagesAccess,
  userHasTabAccess
} from 'utils/permissions/permissions-apply';
import { PERMISSIONS_MODULES_ID } from 'constants/team-members';

import Pixels from 'components/Pixels/Pixels';
import Pages from 'components/Pages/Pages';
import CreateEditPage from 'components/Pages/components/CreateEditPage/CreateEditPage';
import TeamMembers from 'components/NewTeamMembers/TeamMembers';
import CreateEditMember from 'components/NewTeamMembers/components/CreateEditMember/CreateEditMember';

const userInfo = JSON.parse(localStorage.getItem('userInfo'));
const isSaudi =
  userInfo?.user?.country?.code === COUNTRIES[1].codeName || false;

const Analytics = lazy(
  async () =>
    await retry(async () => await import('components/Analytics/Analytics'))
);
const CreateEditOrder = lazy(
  async () =>
    await retry(
      async () =>
        await import(
          'components/Orders/components/CreateEditOrder/CreateEditOrder'
        )
    )
);
const OrderSummary = lazy(
  async () =>
    await retry(
      async () =>
        await import('components/Orders/components/OrderSummary/OrderSummary')
    )
);
const FlyersShop = lazy(
  async () =>
    await retry(async () => await import('components/FlyersShop/FlyersShop'))
);
const Borrow = lazy(
  async () => await retry(async () => await import('components/Borrow/Borrow'))
);
const InternationalShipping = lazy(() =>
  retry(() => import('components/InternationalShipping/InternationalShipping'))
);
const PaymentRedirect = lazy(
  async () =>
    await retry(
      async () => await import('components/PaymentRedirect/PaymentRedirect')
    )
);
const Orders = lazy(
  async () =>
    await retry(async () => await import('components/Orders/OrdersContainer'))
);
const SupporTickets = lazy(
  async () =>
    await retry(
      async () => await import('components/SupportTickets/SupportTickets')
    )
);
const Pickups = lazy(
  async () =>
    await retry(async () => await import('components/Pickups/Pickups'))
);
const Overview = lazy(
  async () =>
    await retry(async () => await import('components/Overview/Overview'))
);
const Inventory = lazy(
  async () =>
    await retry(
      async () => await import('components/Fulfillment/FulfillmentContainer')
    )
);
const NewSettings = lazy(
  async () =>
    await retry(async () => await import('components/Settings/Settings'))
);

const Wallet = lazy(
  async () =>
    await retry(async () => await import('./components/Wallet/Wallet'))
);

const Products = lazy(
  async () =>
    await retry(
      async () => await import('./components/Products/ProductsContainer')
    )
);

const SllrStore = lazy(
  async () =>
    await retry(
      async () =>
        await import('components/Products/components/SllrStore/SllrStore')
    )
);

const StoreDesign = lazy(
  async () =>
    await retry(
      async () => await import('./storeDesignBuilder/StoreDesignRoot')
    )
);

const Customers = lazy(
  async () =>
    await retry(async () => await import('components/Customers/Cutomers'))
);

const Discounts = lazy(
  async () =>
    await retry(async () => await import('components/Discounts/Discounts'))
);

const Chats = lazy(
  async () => await retry(async () => await import('components/Chats/Chats'))
);

// https://github.com/ReactTraining/react-router/tree/master/packages/react-router-config

const {
  DELIVERIES,
  PRODUCTS,
  DISCOUNTS,
  PAGES,
  STORE_DESIGN,
  APP_STORE,
  ONLINE_STORE,
  TEAM_MEMBERS,
  ANALYTICS,
  WALLET,
  SETTINGS
} = PERMISSIONS_MODULES_ID;

const routes = (businessInfo) => {
  const isFirstOrderCreated = businessInfo?.isFirstOrderCreated;
  const isFirstPickupCreated = businessInfo?.isFirstPickupCreated;
  const businessId = userInfo?.user?.businessAdminInfo?.businessId;
  const userSllrPermissions = businessInfo?.userSllrPermissions;

  const { HIDE_CREATE_ORDER, HIDE_PACKAGING_TAB, HIDE_EDIT_ORDER } =
    getOrdersAndPickupsAccess(userSllrPermissions);

  const { HIDE_CREATE_PAGE } = getPagesAccess(userSllrPermissions);

  const userHasOrdersAndPickupsAccess = userHasTabAccess(
    DELIVERIES,
    userSllrPermissions
  );

  const firstPickupCreatedORNotSllrUser = !isSllrUser() || isFirstPickupCreated;

  return [
    {
      path: '/orders',
      // exact: true,
      name: 'Orders',
      component: Orders,
      access: userHasOrdersAndPickupsAccess
    },
    {
      path: '/create-order',
      exact: true,
      name: 'NewShipment',
      access: firstPickupCreatedORNotSllrUser && !HIDE_CREATE_ORDER,
      component: CreateEditOrder
    },
    {
      path: '/create-order/order-summary',
      exact: true,
      name: 'OrderSummary',
      component: OrderSummary,
      access: userHasOrdersAndPickupsAccess
    },
    {
      path: '/edit-order/:id',
      exact: true,
      name: 'NewShipment',
      component: CreateEditOrder,
      access: !HIDE_EDIT_ORDER
    },
    {
      path: '/pickups',
      // exact: true,
      name: 'Pickups',
      component: Pickups,
      access:
        userHasOrdersAndPickupsAccess && !(isSllrUser() && !isFirstOrderCreated)
    },
    {
      path: '/products',
      name: 'Products',
      component: Products,
      access: userHasTabAccess(PRODUCTS, userSllrPermissions)
    },
    {
      path: '/customers',
      name: 'Customers',
      component: Customers,
      access: true
    },
    {
      path: '/discounts',
      name: 'Discounts',
      component: Discounts,
      access: userHasTabAccess(DISCOUNTS, userSllrPermissions)
    },
    {
      path: '/chats',
      name: 'Chats',
      component: Chats,
      access: true
    },
    {
      path: '/sllr',
      name: 'Sllr',
      component: SllrStore,
      access: userHasTabAccess(ONLINE_STORE, userSllrPermissions)
    },
    {
      path: '/thank-you',
      exact: true,
      name: 'thank-you',
      component: Overview,
      access: true
    },
    {
      path: '/overview',
      // exact: true,
      name: 'Overview',
      component: Overview,
      access: firstPickupCreatedORNotSllrUser
    },
    // {
    //   path: '/fulfillment',
    //   exact: true,
    //   name: 'Fulfillment',
    //   component: Fulfillment,
    //   access: true
    // },
    {
      path: '/inventory',
      name: 'Inventory',
      component: Inventory,
      access: firstPickupCreatedORNotSllrUser
    },
    {
      path: '/box',
      // exact: true,
      name: 'BostaBox',
      component: InternationalShipping,
      access: !isSaudi
    },
    {
      path: '/borrow',
      name: 'Borrow',
      component: Borrow,
      access: []
    },
    {
      path: '/payment',
      exact: true,
      name: 'Payment',
      component: PaymentRedirect,
      access: true
    },
    {
      path: '/shop',
      exact: true,
      name: 'BostaShop',
      component: FlyersShop,
      access: !HIDE_PACKAGING_TAB
    },
    {
      path: '/wallet',
      // exact: true,
      name: 'Wallet',
      component: Wallet,
      access:
        userHasTabAccess(WALLET, userSllrPermissions) &&
        firstPickupCreatedORNotSllrUser
    },
    {
      path: '/analytics',
      name: 'Analytics',
      component: Analytics,
      access:
        userHasTabAccess(ANALYTICS, userSllrPermissions) &&
        firstPickupCreatedORNotSllrUser
    },
    {
      path: '/support-tickets',
      exact: true,
      name: 'support',
      component: SupporTickets,
      access: firstPickupCreatedORNotSllrUser
    },
    {
      path: '/settings',
      name: 'Settings',
      component: NewSettings,
      access: userHasTabAccess(SETTINGS, userSllrPermissions)
    },
    {
      path: '/store-design',
      name: 'StoreDesign',
      component: StoreDesign,
      access: userHasTabAccess(STORE_DESIGN, userSllrPermissions)
    },
    {
      path: '/appstore',
      exact: true,
      name: 'pixels',
      component: Pixels,
      access: userHasTabAccess(APP_STORE, userSllrPermissions)
    },
    {
      path: '/pages',
      exact: true,
      name: 'pages',
      component: Pages,
      access: userHasTabAccess(PAGES, userSllrPermissions)
    },
    {
      path: '/pages/create',
      exact: true,
      name: 'Create Page',
      component: CreateEditPage,
      access: userHasTabAccess(PAGES, userSllrPermissions) && !HIDE_CREATE_PAGE
    },
    {
      path: '/pages/edit/:id',
      exact: true,
      name: 'Edit Page',
      component: CreateEditPage,
      access: userHasTabAccess(PAGES, userSllrPermissions) && !HIDE_CREATE_PAGE
    },
    {
      path: '/team-members',
      exact: true,
      name: 'Team Members',
      component: TeamMembers,
      access: userHasTabAccess(TEAM_MEMBERS, userSllrPermissions)
    },
    {
      path: '/team-members/create',
      exact: true,
      name: 'Create Member',
      component: CreateEditMember,
      access: userHasTabAccess(TEAM_MEMBERS, userSllrPermissions)
    },
    {
      path: '/team-members/edit/:id',
      exact: true,
      name: 'Edit Member',
      component: CreateEditMember,
      access: userHasTabAccess(TEAM_MEMBERS, userSllrPermissions)
    }
  ];
};

export default routes;
